import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FaClipboardList, FaBan, FaExclamationTriangle, FaLightbulb,
  FaExchangeAlt, FaPills, FaSave, FaUndo, FaEdit, FaPlus,
  FaTimes, FaClock, FaStickyNote, FaCheck, FaUserShield,
  FaLock, FaUnlock, FaShieldAlt, FaList
} from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: convert user_clinical_summary structure to editable state
function summaryToEditable(summary) {
  if (!summary) return null;
  return {
    summary_intro: summary.summary_intro || '',
    conditions_identified: summary.conditions_identified || [],
    avoid_foods: summary.avoid_foods || [],
    recommendations: summary.recommendations || [],
    action_plans: (summary.action_plans || []).map(ap => ({
      title: ap.title || '',
      clinical_objective: ap.clinical_objective || '',
      actions: ap.actions || [],
      monitoring_points: ap.monitoring_points || [],
      follow_up: ap.follow_up || ''
    })),
    substitutions: (summary.substitutions || []).map(s => ({
      original: s.original || '',
      replacements: Array.isArray(s.replacements) ? s.replacements : []
    })),
    recommended_supplements: (summary.recommended_supplements || []).map(s => ({
      name: s.name || '',
      dose: s.dose || '',
      dose_mode: s.dose_mode || 'range',
      frequency: s.frequency || '',
      timing: s.timing || '',
      requires_validation: s.requires_validation || false,
      clinical_note: s.clinical_note || s.reason || '',
      reason: s.clinical_note || s.reason || ''
    })),
    avoid_supplements: (summary.avoid_supplements || []).map(s => ({
      name: s.name || '',
      reason: s.reason || '',
      requires_validation: s.requires_validation || false,
      clinical_note: s.clinical_note || s.reason || ''
    })),
    manual_notes: summary.manual_notes || ''
  };
}

// Helper: build from clinicalResult (raw auto-generated data)
function clinicalResultToEditable(result) {
  if (!result) return null;
  return {
    summary_intro: result.user_summary?.summary_intro || '',
    conditions_identified: result.user_summary?.conditions_identified || (result.conditions_applied || []).map(c => c.label),
    avoid_foods: result.avoid_foods_display || [],
    recommendations: (result.active_notes || []).map(n => n.body || ''),
    action_plans: (result.active_actions || []).map(a => ({
      title: a.title || '',
      clinical_objective: a.clinical_objective || '',
      actions: a.actions || [],
      monitoring_points: a.monitoring_points || [],
      follow_up: a.follow_up || ''
    })),
    substitutions: Object.entries(result.substitution_map || {}).slice(0, 12).map(([k, v]) => ({
      original: k,
      replacements: Array.isArray(v) ? v.slice(0, 3) : [v]
    })),
    recommended_supplements: (result.recommended_supplements || []).map(s => ({
      name: s.name || '',
      dose: s.dose || s.dose_range || '',
      dose_mode: s.dose_mode || 'range',
      frequency: s.frequency || '',
      timing: s.timing || '',
      requires_validation: s.requires_validation || false,
      clinical_note: s.clinical_note || s.reason || '',
      reason: s.clinical_note || s.reason || ''
    })),
    avoid_supplements: (result.avoid_supplements || []).map(s => ({
      name: s.name || '',
      reason: s.reason || '',
      requires_validation: s.requires_validation || false,
      clinical_note: s.clinical_note || s.reason || ''
    })),
    manual_notes: ''
  };
}


// ========== SECTION COMPONENTS ==========

const EditableSection = ({ title, icon: Icon, iconColor, bgColor, borderColor, count, children, testId }) => {
  // 2026-02 · Persistir estado colapsado/expandido en localStorage por sección.
  // La clave usa `testId` (estable) o el título como fallback.
  const storageKey = `clinical_edit_section_collapsed__${testId || title || 'section'}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === '1') return true;
      if (raw === '0') return false;
    } catch (e) { /* localStorage no disponible */ }
    return false;
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? '1' : '0');
    } catch (e) { /* localStorage no disponible */ }
  }, [collapsed, storageKey]);
  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}` }}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full p-2.5 flex items-center justify-between hover:brightness-110 transition-all"
        data-testid={testId}
      >
        <p className="font-bold text-xs flex items-center gap-2" style={{ color: iconColor }}>
          <Icon style={{ color: iconColor }} />
          {title}
          {count > 0 && (
            <span className="px-2 py-0.5 rounded text-xs" style={{ background: `${iconColor}30` }}>{count}</span>
          )}
        </p>
        <span className="text-xs" style={{ color: iconColor }}>{collapsed ? '+ Expandir' : '- Colapsar'}</span>
      </button>
      {!collapsed && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

const EditableListItem = ({ value, onChange, onRemove, placeholder }) => (
  <div className="flex items-center gap-2 mb-1">
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-gray-900/50 border-gray-700 text-white text-xs flex-1"
    />
    <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1 flex-shrink-0" data-testid="remove-item-btn">
      <FaTimes className="text-xs" />
    </button>
  </div>
);

const AddItemButton = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 text-xs text-gray-400 hover:text-white mt-1 px-2 py-1 rounded border border-dashed border-gray-600 hover:border-gray-400 transition-colors"
    data-testid="add-item-btn"
  >
    <FaPlus className="text-xs" /> {label}
  </button>
);


// ========== INDIVIDUALIZATION PANEL ==========

const IndividualizationPanel = ({ editingPlanId, clinicalResult, onFoodReauthorized, onFoodReblocked }) => {
  const [savedBlockedFoods, setSavedBlockedFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reauthorizing, setReauthorizing] = useState(null);
  const [reauthorForm, setReauthorForm] = useState({ reason: '', clinical_observation: '', observed_tolerance: '', tolerated_quantity: '' });

  const loadBlockedFoods = useCallback(async () => {
    if (!editingPlanId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/clinical-module/blocked-foods/${editingPlanId}`);
      if (res.data?.success) {
        setSavedBlockedFoods(res.data.blocked_foods || []);
      }
    } catch (e) {
      console.error('Error loading blocked foods:', e);
    } finally {
      setLoading(false);
    }
  }, [editingPlanId]);

  useEffect(() => { loadBlockedFoods(); }, [loadBlockedFoods]);

  // Merge: use saved data if plan exists, otherwise use clinicalResult data
  const blockedFoods = useMemo(() => {
    if (savedBlockedFoods.length > 0) return savedBlockedFoods;
    
    // Build from clinicalResult when plan is not yet saved or has no blocked_foods
    if (!clinicalResult) return [];
    const blocked = clinicalResult.blocked_foods || [];
    const behaviorMap = clinicalResult.block_behavior_map || {};
    
    return blocked.map(foodName => {
      const blockType = behaviorMap[foodName] || 'conditional_block';
      return {
        food_name: foodName,
        block_type: blockType,
        can_reauthorize: true,
        is_reauthorized: false,
        reauthorization_data: null
      };
    });
  }, [savedBlockedFoods, clinicalResult]);

  const handleReauthorize = async (foodName) => {
    if (!editingPlanId) {
      toast.error('Debes guardar el plan primero para reautorizar alimentos');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/clinical-module/reauthorize-foods/${editingPlanId}`, {
        foods: [{
          food_name: foodName,
          ...reauthorForm
        }]
      });
      if (res.data?.success) {
        if (res.data.rejected?.length > 0) {
          toast.error(res.data.rejected[0].reason);
        } else {
          toast.success(`${foodName} reautorizado`);
          if (onFoodReauthorized) onFoodReauthorized(foodName);
        }
        setReauthorizing(null);
        setReauthorForm({ reason: '', clinical_observation: '', observed_tolerance: '', tolerated_quantity: '' });
        loadBlockedFoods();
      }
    } catch (e) {
      toast.error('Error al reautorizar');
    }
  };

  const handleRemoveReauth = async (foodName) => {
    if (!editingPlanId) return;
    try {
      await axios.delete(`${API}/api/clinical-module/reauthorize-foods/${editingPlanId}/${encodeURIComponent(foodName)}`);
      toast.success(`Reautorizacion de ${foodName} eliminada`);
      if (onFoodReblocked) onFoodReblocked(foodName);
      loadBlockedFoods();
    } catch (e) {
      toast.error('Error al eliminar reautorizacion');
    }
  };

  if (blockedFoods.length === 0) return null;

  const reauthorizedCount = blockedFoods.filter(f => f.is_reauthorized).length;

  return (
    <EditableSection
      title="INDIVIDUALIZACION - EXCEPCIONES POR TOLERANCIA"
      icon={FaUserShield}
      iconColor="#10b981"
      bgColor="rgba(16, 185, 129, 0.08)"
      borderColor="rgba(16, 185, 129, 0.3)"
      count={reauthorizedCount}
      testId="clinical-individualization"
    >
      <p className="text-emerald-300/60 text-xs mb-2">
        Alimentos bloqueados por el modulo clinico. Puedes reautorizar los que tengan buena tolerancia individual.
        {!editingPlanId && <span className="text-amber-400 ml-1">(Guarda el plan primero para poder reautorizar)</span>}
      </p>
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {blockedFoods.map((food, i) => (
          <div key={i}>
            <div className="flex items-center gap-2 p-1.5 rounded bg-gray-800/50 border border-gray-700/50">
              {food.is_reauthorized ? (
                <FaUnlock className="text-emerald-400 text-xs flex-shrink-0" title="Reautorizado" />
              ) : (
                <FaShieldAlt className="text-amber-400 text-xs flex-shrink-0" title="Bloqueo condicional" />
              )}
              <span className={`text-xs flex-1 ${food.is_reauthorized ? 'text-emerald-300' : 'text-amber-300'}`}>
                {food.food_name}
              </span>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {food.block_type === 'absolute_block' ? 'absoluto' : food.block_type === 'tolerance_based_block' ? 'tolerancia' : 'condicional'}
              </span>
              {food.is_reauthorized ? (
                <button
                  onClick={() => handleRemoveReauth(food.food_name)}
                  className="text-red-400 hover:text-red-300 text-xs px-1.5 py-0.5 rounded border border-red-500/30 hover:bg-red-500/10"
                  data-testid={`remove-reauth-${i}`}
                >
                  Revocar
                </button>
              ) : (
                <button
                  onClick={() => setReauthorizing(reauthorizing === food.food_name ? null : food.food_name)}
                  className="text-emerald-400 hover:text-emerald-300 text-xs px-1.5 py-0.5 rounded border border-emerald-500/30 hover:bg-emerald-500/10"
                  data-testid={`reauthorize-${i}`}
                >
                  {reauthorizing === food.food_name ? 'Cerrar' : 'Reautorizar'}
                </button>
              )}
            </div>
            {reauthorizing === food.food_name && (
              <div className="mt-1 ml-4 p-3 bg-gray-900 border border-emerald-500/30 rounded-lg" data-testid={`reauth-form-${i}`}>
                <p className="text-emerald-300 text-xs font-bold mb-2">Reautorizar: {food.food_name}</p>
                <div className="space-y-1.5">
                  <Input value={reauthorForm.reason} onChange={e => setReauthorForm(p => ({...p, reason: e.target.value}))} placeholder="Motivo de reautorizacion..." className="bg-gray-800 border-gray-700 text-white text-xs" />
                  <Input value={reauthorForm.clinical_observation} onChange={e => setReauthorForm(p => ({...p, clinical_observation: e.target.value}))} placeholder="Observacion clinica..." className="bg-gray-800 border-gray-700 text-white text-xs" />
                  <Input value={reauthorForm.observed_tolerance} onChange={e => setReauthorForm(p => ({...p, observed_tolerance: e.target.value}))} placeholder="Tolerancia observada..." className="bg-gray-800 border-gray-700 text-white text-xs" />
                  <Input value={reauthorForm.tolerated_quantity} onChange={e => setReauthorForm(p => ({...p, tolerated_quantity: e.target.value}))} placeholder="Cantidad tolerada..." className="bg-gray-800 border-gray-700 text-white text-xs" />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleReauthorize(food.food_name)} data-testid={`confirm-reauth-${i}`}>Confirmar</Button>
                    <Button size="sm" variant="outline" className="text-gray-300 border-gray-600 text-xs" onClick={() => setReauthorizing(null)}>Cancelar</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {loading && <p className="text-gray-400 text-xs mt-2">Cargando...</p>}
    </EditableSection>
  );
};


// ========== MAIN COMPONENT ==========

const ClinicalEditPanel = ({
  clinicalResult,
  clinicalNotes,
  setClinicalNotes,
  adminBlockedFoodsText,
  setAdminBlockedFoodsText,
  editingPlanId,
  protocolStatus,
  collapsedSections,
  toggleSection,
  loadedEditedData,   // clinical_data_edited from saved plan
  loadedAutoSummary,  // user_clinical_summary from saved plan
  onEditsSaved,       // callback after saving edits
  onFoodReauthorized, // callback when food is reauthorized (remove from blocked)
  onFoodReblocked     // callback when reauthorization is revoked (add back to blocked)
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  // Initialize edit data from auto-generated or loaded edited data
  useEffect(() => {
    if (loadedEditedData && Object.keys(loadedEditedData).length > 0) {
      const autoData = loadedAutoSummary
        ? summaryToEditable(loadedAutoSummary)
        : clinicalResultToEditable(clinicalResult);
      if (autoData) {
        const merged = { ...autoData };
        Object.keys(loadedEditedData).forEach(key => {
          if (loadedEditedData[key] !== undefined && loadedEditedData[key] !== null) {
            merged[key] = loadedEditedData[key];
          }
        });
        setEditData(merged);
      }
      setHasEdits(true);
    } else if (clinicalResult) {
      setEditData(clinicalResultToEditable(clinicalResult));
      setHasEdits(false);
    }
  }, [clinicalResult, loadedEditedData, loadedAutoSummary]);

  // Sync clinicalNotes with editData
  useEffect(() => {
    if (editData && clinicalNotes !== undefined) {
      setEditData(prev => prev ? { ...prev, manual_notes: clinicalNotes } : prev);
    }
  }, [clinicalNotes]);

  const updateField = useCallback((field, value) => {
    setEditData(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateListItem = useCallback((field, index, value) => {
    setEditData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] || [])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  }, []);

  const removeListItem = useCallback((field, index) => {
    setEditData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] || [])];
      arr.splice(index, 1);
      return { ...prev, [field]: arr };
    });
  }, []);

  const addListItem = useCallback((field, defaultValue) => {
    setEditData(prev => {
      if (!prev) return prev;
      return { ...prev, [field]: [...(prev[field] || []), defaultValue] };
    });
  }, []);

  // Save clinical edits to backend
  const handleSaveEdits = async () => {
    if (!editingPlanId || !editData) {
      toast.error('Debes guardar el plan primero antes de editar el modulo clinico');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        summary_intro: editData.summary_intro,
        avoid_foods: editData.avoid_foods,
        recommendations: editData.recommendations,
        action_plans: editData.action_plans,
        substitutions: editData.substitutions,
        recommended_supplements: editData.recommended_supplements,
        avoid_supplements: editData.avoid_supplements,
        manual_notes: editData.manual_notes || clinicalNotes || ''
      };
      await axios.post(`${API}/api/clinical-module/save-clinical-edits/${editingPlanId}`, payload);
      toast.success('Ediciones clinicas guardadas correctamente');
      setHasEdits(true);
      setEditMode(false);
      if (onEditsSaved) onEditsSaved(payload);
    } catch (error) {
      console.error('Error saving clinical edits:', error);
      toast.error('Error al guardar las ediciones clinicas');
    } finally {
      setSaving(false);
    }
  };

  // Restore auto-generated content
  const handleRestore = async () => {
    if (!editingPlanId) {
      toast.error('No hay plan guardado para restaurar');
      return;
    }
    if (!window.confirm('Restaurar la version generada automaticamente? Se perderan todas las ediciones manuales.')) return;
    setRestoring(true);
    try {
      await axios.post(`${API}/api/clinical-module/restore-clinical-auto/${editingPlanId}`);
      toast.success('Version automatica restaurada');
      if (clinicalResult) {
        setEditData(clinicalResultToEditable(clinicalResult));
      }
      setHasEdits(false);
      setEditMode(false);
      if (onEditsSaved) onEditsSaved(null);
    } catch (error) {
      console.error('Error restoring clinical auto:', error);
      toast.error('Error al restaurar version automatica');
    } finally {
      setRestoring(false);
    }
  };

  if (!clinicalResult || !editData) return null;

  return (
    <Card className="border-2 border-red-500/60 bg-gradient-to-r from-red-950/40 to-orange-950/30" data-testid="clinical-edit-panel">
      <CardContent className="py-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-500 text-white">
              <FaClipboardList />
            </div>
            <div>
              <h3 className="font-bold text-red-400 text-sm">MODULO CLINICO ACTIVO</h3>
              <p className="text-xs text-red-300/70">
                Condicion dominante: <span className="font-bold text-white">{clinicalResult.dominant_condition?.label}</span>
                {clinicalResult.conditions_applied?.length > 1 && (
                  <span className="ml-1">+ {clinicalResult.conditions_applied.length - 1} mas</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-red-500/30 text-red-300 border-red-500/50">
              {clinicalResult.blocked_foods?.length || 0} bloqueados
            </Badge>
            <Badge className="bg-amber-500/30 text-amber-300 border-amber-500/50">
              {clinicalResult.caution_foods?.length || 0} precaucion
            </Badge>
            {hasEdits && (
              <Badge className="bg-purple-500/30 text-purple-300 border-purple-500/50" data-testid="edited-badge">
                <FaEdit className="mr-1" /> Editado
              </Badge>
            )}
          </div>
        </div>

        {/* Protocol status */}
        {protocolStatus && (
          <div className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
            <div className="flex-1 flex items-center gap-2">
              <FaClock className="text-indigo-400 text-sm" />
              <span className="text-xs text-indigo-300 font-medium">
                Semana {protocolStatus.current_week} - {protocolStatus.phase}
              </span>
            </div>
            {protocolStatus.needs_update && (
              <Badge className="bg-amber-500/30 text-amber-300 border-amber-500/50 animate-pulse text-xs">
                Necesita Actualizacion
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              Prox. revision: {protocolStatus.next_update_date ? new Date(protocolStatus.next_update_date).toLocaleDateString('es-CO') : 'N/A'}
            </span>
          </div>
        )}

        {/* Edit mode toggle + action buttons */}
        <div className="flex items-center gap-2 border-t border-gray-700/50 pt-3">
          {!editMode ? (
            <Button
              size="sm"
              onClick={() => setEditMode(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs gap-1"
              data-testid="clinical-edit-mode-btn"
            >
              <FaEdit /> Editar Modulo Clinico
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                onClick={handleSaveEdits}
                disabled={saving || !editingPlanId}
                className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1"
                data-testid="clinical-save-edits-btn"
              >
                <FaSave /> {saving ? 'Guardando...' : 'Guardar Cambios Clinicos'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditMode(false);
                  if (loadedEditedData && Object.keys(loadedEditedData).length > 0) {
                    const autoData = loadedAutoSummary
                      ? summaryToEditable(loadedAutoSummary)
                      : clinicalResultToEditable(clinicalResult);
                    if (autoData) {
                      const merged = { ...autoData };
                      Object.keys(loadedEditedData).forEach(key => {
                        if (loadedEditedData[key] !== undefined) merged[key] = loadedEditedData[key];
                      });
                      setEditData(merged);
                    }
                  } else if (clinicalResult) {
                    setEditData(clinicalResultToEditable(clinicalResult));
                  }
                }}
                className="text-gray-300 border-gray-600 text-xs gap-1"
                data-testid="clinical-cancel-edit-btn"
              >
                <FaTimes /> Cancelar
              </Button>
            </>
          )}
          {hasEdits && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRestore}
              disabled={restoring}
              className="text-amber-300 border-amber-600 hover:bg-amber-900/30 text-xs gap-1 ml-auto"
              data-testid="clinical-restore-auto-btn"
            >
              <FaUndo /> {restoring ? 'Restaurando...' : 'Restaurar Version Automatica'}
            </Button>
          )}
          {!editingPlanId && editMode && (
            <p className="text-xs text-amber-400 ml-2">Guarda el plan primero para poder guardar ediciones clinicas</p>
          )}
        </div>

        {/* ===== BLOCK 1: AJUSTES CLINICOS (RESUMEN) ===== */}
        <EditableSection
          title="AJUSTES CLINICOS DE TU PLAN"
          icon={FaClipboardList}
          iconColor="#f87171"
          bgColor="rgba(239, 68, 68, 0.08)"
          borderColor="rgba(239, 68, 68, 0.25)"
          testId="clinical-edit-summary"
        >
          {editMode ? (
            <Textarea
              value={editData.summary_intro}
              onChange={(e) => updateField('summary_intro', e.target.value)}
              className="bg-gray-900/50 border-red-500/30 text-white text-xs min-h-[60px]"
              placeholder="Resumen clinico del caso..."
              data-testid="clinical-edit-summary-input"
            />
          ) : (
            <p className="text-red-200/80 text-xs leading-relaxed">{editData.summary_intro || 'Sin resumen'}</p>
          )}
        </EditableSection>

        {/* ===== BLOCK 2: CONDICIONES IDENTIFICADAS ===== */}
        <EditableSection
          title="CONDICIONES IDENTIFICADAS"
          icon={FaList}
          iconColor="#f59e0b"
          bgColor="rgba(245, 158, 11, 0.08)"
          borderColor="rgba(245, 158, 11, 0.25)"
          count={clinicalResult.conditions_applied?.length}
          testId="clinical-conditions-identified"
        >
          <div className="flex flex-wrap gap-1.5">
            {clinicalResult.conditions_applied?.map((c, i) => (
              <span key={i} className="bg-amber-500/20 text-amber-300 px-2 py-1 rounded text-xs border border-amber-500/30">
                {c.label}
              </span>
            ))}
          </div>
        </EditableSection>

        {/* ===== BLOCK 3: ALIMENTOS A EVITAR ===== */}
        <EditableSection
          title="ALIMENTOS A EVITAR O CONTROLAR"
          icon={FaBan}
          iconColor="#f97316"
          bgColor="rgba(249, 115, 22, 0.1)"
          borderColor="rgba(249, 115, 22, 0.3)"
          count={editData.avoid_foods?.length}
          testId="clinical-edit-avoid-foods"
        >
          {editMode ? (
            <div>
              {editData.avoid_foods.map((food, i) => (
                <EditableListItem
                  key={i}
                  value={food}
                  onChange={(v) => updateListItem('avoid_foods', i, v)}
                  onRemove={() => removeListItem('avoid_foods', i)}
                  placeholder="Alimento a evitar..."
                />
              ))}
              <AddItemButton onClick={() => addListItem('avoid_foods', '')} label="Agregar alimento" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {editData.avoid_foods.map((food, i) => (
                <span key={i} className="bg-orange-500/20 text-orange-300 px-2 py-1 rounded text-xs border border-orange-500/30">
                  {food}
                </span>
              ))}
              {editData.avoid_foods.length === 0 && <p className="text-orange-300/50 text-xs">Sin alimentos a evitar especificos</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 4: RECOMENDACIONES IMPORTANTES ===== */}
        <EditableSection
          title="RECOMENDACIONES IMPORTANTES"
          icon={FaLightbulb}
          iconColor="#fbbf24"
          bgColor="rgba(251, 191, 36, 0.08)"
          borderColor="rgba(251, 191, 36, 0.25)"
          count={editData.recommendations?.length}
          testId="clinical-edit-recommendations"
        >
          {editMode ? (
            <div className="space-y-2">
              {editData.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Textarea
                    value={rec}
                    onChange={(e) => updateListItem('recommendations', i, e.target.value)}
                    className="bg-gray-900/50 border-yellow-500/30 text-white text-xs min-h-[40px] flex-1"
                    placeholder="Recomendacion..."
                  />
                  <button onClick={() => removeListItem('recommendations', i)} className="text-red-400 hover:text-red-300 p-1 mt-1">
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
              <AddItemButton onClick={() => addListItem('recommendations', '')} label="Agregar recomendacion" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {editData.recommendations.map((rec, i) => (
                <div key={i} className="p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-yellow-200/80 text-xs leading-relaxed">{rec}</p>
                </div>
              ))}
              {editData.recommendations.length === 0 && <p className="text-yellow-300/50 text-xs">Sin recomendaciones especificas</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 5: PLAN DE ACCION INICIAL ===== */}
        <EditableSection
          title="PLAN DE ACCION INICIAL"
          icon={FaCheck}
          iconColor="#22c55e"
          bgColor="rgba(34, 197, 94, 0.1)"
          borderColor="rgba(34, 197, 94, 0.3)"
          count={editData.action_plans?.length}
          testId="clinical-edit-action-plans"
        >
          {editMode ? (
            <div className="space-y-3">
              {editData.action_plans.map((plan, i) => (
                <div key={i} className="p-2 rounded bg-green-500/10 border border-green-500/20 space-y-2">
                  <div className="flex items-center justify-between">
                    <Input
                      value={plan.title}
                      onChange={(e) => {
                        const updated = [...editData.action_plans];
                        updated[i] = { ...updated[i], title: e.target.value };
                        updateField('action_plans', updated);
                      }}
                      className="bg-gray-900/50 border-green-500/30 text-white text-xs font-semibold"
                      placeholder="Titulo del protocolo..."
                    />
                    <button onClick={() => removeListItem('action_plans', i)} className="text-red-400 hover:text-red-300 p-1 ml-2">
                      <FaTimes className="text-xs" />
                    </button>
                  </div>
                  <Input
                    value={plan.clinical_objective}
                    onChange={(e) => {
                      const updated = [...editData.action_plans];
                      updated[i] = { ...updated[i], clinical_objective: e.target.value };
                      updateField('action_plans', updated);
                    }}
                    className="bg-gray-900/50 border-green-500/20 text-green-200/60 text-xs italic"
                    placeholder="Objetivo clinico inicial..."
                  />
                  <Textarea
                    value={(plan.actions || []).join('\n')}
                    onChange={(e) => {
                      const updated = [...editData.action_plans];
                      updated[i] = { ...updated[i], actions: e.target.value.split('\n').filter(a => a.trim()) };
                      updateField('action_plans', updated);
                    }}
                    className="bg-gray-900/50 border-green-500/20 text-white text-xs min-h-[50px]"
                    placeholder="Acciones prioritarias (una por linea)..."
                  />
                  <Input
                    value={(plan.monitoring_points || []).join(', ')}
                    onChange={(e) => {
                      const updated = [...editData.action_plans];
                      updated[i] = { ...updated[i], monitoring_points: e.target.value.split(',').map(m => m.trim()).filter(Boolean) };
                      updateField('action_plans', updated);
                    }}
                    className="bg-gray-900/50 border-green-500/20 text-green-300/50 text-xs"
                    placeholder="Monitoreo sugerido (separado por comas)..."
                  />
                  <Input
                    value={plan.follow_up}
                    onChange={(e) => {
                      const updated = [...editData.action_plans];
                      updated[i] = { ...updated[i], follow_up: e.target.value };
                      updateField('action_plans', updated);
                    }}
                    className="bg-gray-900/50 border-green-500/20 text-green-400/50 text-xs"
                    placeholder="Seguimiento segun evolucion..."
                  />
                </div>
              ))}
              <AddItemButton
                onClick={() => addListItem('action_plans', { title: '', clinical_objective: '', actions: [], monitoring_points: [], follow_up: '' })}
                label="Agregar plan de accion"
              />
            </div>
          ) : (
            <div className="space-y-2">
              {editData.action_plans.map((action, i) => (
                <div key={i} className="p-2 rounded bg-green-500/10 border border-green-500/20">
                  <p className="text-green-300 font-semibold text-xs mb-1">{action.title}</p>
                  {action.clinical_objective && (
                    <p className="text-green-200/60 text-xs italic mb-1.5">{action.clinical_objective}</p>
                  )}
                  {action.actions?.length > 0 && (
                    <ul className="list-disc list-inside space-y-0.5 mb-1">
                      {action.actions.map((a, j) => (
                        <li key={j} className="text-green-200/80 text-xs">{a}</li>
                      ))}
                    </ul>
                  )}
                  {action.monitoring_points?.length > 0 && (
                    <p className="text-green-300/50 text-xs">Monitoreo: {action.monitoring_points.join(', ')}</p>
                  )}
                  {action.follow_up && (
                    <p className="text-green-400/50 text-xs mt-1">Seguimiento: {action.follow_up}</p>
                  )}
                </div>
              ))}
              {editData.action_plans.length === 0 && <p className="text-green-300/50 text-xs">Sin plan de accion definido</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 6: SUSTITUCIONES RECOMENDADAS ===== */}
        <EditableSection
          title="SUSTITUCIONES RECOMENDADAS"
          icon={FaExchangeAlt}
          iconColor="#3b82f6"
          bgColor="rgba(59, 130, 246, 0.1)"
          borderColor="rgba(59, 130, 246, 0.3)"
          count={editData.substitutions?.length}
          testId="clinical-edit-substitutions"
        >
          {editMode ? (
            <div className="space-y-2">
              {editData.substitutions.map((sub, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={sub.original}
                    onChange={(e) => {
                      const updated = [...editData.substitutions];
                      updated[i] = { ...updated[i], original: e.target.value };
                      updateField('substitutions', updated);
                    }}
                    className="bg-gray-900/50 border-blue-500/30 text-red-400 text-xs w-1/3"
                    placeholder="Alimento original..."
                  />
                  <span className="text-blue-400 text-xs flex-shrink-0">&rarr;</span>
                  <Input
                    value={(sub.replacements || []).join(', ')}
                    onChange={(e) => {
                      const updated = [...editData.substitutions];
                      updated[i] = { ...updated[i], replacements: e.target.value.split(',').map(r => r.trim()).filter(Boolean) };
                      updateField('substitutions', updated);
                    }}
                    className="bg-gray-900/50 border-blue-500/30 text-blue-300 text-xs flex-1"
                    placeholder="Reemplazos (separados por coma)..."
                  />
                  <button onClick={() => removeListItem('substitutions', i)} className="text-red-400 hover:text-red-300 p-1">
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
              <AddItemButton onClick={() => addListItem('substitutions', { original: '', replacements: [] })} label="Agregar sustitucion" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {editData.substitutions.map((sub, i) => (
                <div key={i} className="flex items-start gap-2 text-xs p-1.5 rounded bg-blue-500/10">
                  <span className="text-red-400 line-through whitespace-nowrap">{sub.original}</span>
                  <span className="text-blue-400">&rarr;</span>
                  <span className="text-blue-300">{(sub.replacements || []).join(', ')}</span>
                </div>
              ))}
              {editData.substitutions.length === 0 && <p className="text-blue-300/50 text-xs">Sin sustituciones requeridas</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 7: SUPLEMENTACION RECOMENDADA (enhanced) ===== */}
        <EditableSection
          title="SUPLEMENTACION RECOMENDADA"
          icon={FaPills}
          iconColor="#a78bfa"
          bgColor="rgba(167, 139, 250, 0.1)"
          borderColor="rgba(167, 139, 250, 0.3)"
          count={editData.recommended_supplements?.length}
          testId="clinical-edit-rec-supplements"
        >
          {editMode ? (
            <div className="space-y-2">
              {editData.recommended_supplements.map((supp, i) => (
                <div key={i} className="p-2 rounded bg-purple-500/10 border border-purple-500/20 space-y-1">
                  <div className="flex items-center gap-2">
                    <Input
                      value={supp.name}
                      onChange={(e) => {
                        const updated = [...editData.recommended_supplements];
                        updated[i] = { ...updated[i], name: e.target.value };
                        updateField('recommended_supplements', updated);
                      }}
                      className="bg-gray-900/50 border-purple-500/30 text-white text-xs font-semibold flex-1"
                      placeholder="Nombre del suplemento..."
                    />
                    <button onClick={() => removeListItem('recommended_supplements', i)} className="text-red-400 hover:text-red-300 p-1">
                      <FaTimes className="text-xs" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <Input
                      value={supp.dose}
                      onChange={(e) => {
                        const updated = [...editData.recommended_supplements];
                        updated[i] = { ...updated[i], dose: e.target.value };
                        updateField('recommended_supplements', updated);
                      }}
                      className="bg-gray-900/50 border-purple-500/20 text-purple-300 text-xs"
                      placeholder="Dosis o rango..."
                    />
                    <Input
                      value={supp.frequency}
                      onChange={(e) => {
                        const updated = [...editData.recommended_supplements];
                        updated[i] = { ...updated[i], frequency: e.target.value };
                        updateField('recommended_supplements', updated);
                      }}
                      className="bg-gray-900/50 border-purple-500/20 text-purple-300 text-xs"
                      placeholder="Frecuencia..."
                    />
                    <Input
                      value={supp.timing}
                      onChange={(e) => {
                        const updated = [...editData.recommended_supplements];
                        updated[i] = { ...updated[i], timing: e.target.value };
                        updateField('recommended_supplements', updated);
                      }}
                      className="bg-gray-900/50 border-purple-500/20 text-purple-300 text-xs"
                      placeholder="Momento..."
                    />
                  </div>
                  <Input
                    value={supp.clinical_note || supp.reason}
                    onChange={(e) => {
                      const updated = [...editData.recommended_supplements];
                      updated[i] = { ...updated[i], clinical_note: e.target.value, reason: e.target.value };
                      updateField('recommended_supplements', updated);
                    }}
                    className="bg-gray-900/50 border-purple-500/20 text-purple-300/70 text-xs"
                    placeholder="Nota clinica..."
                  />
                </div>
              ))}
              <AddItemButton onClick={() => addListItem('recommended_supplements', { name: '', dose: '', dose_mode: 'range', frequency: '', timing: '', requires_validation: false, clinical_note: '', reason: '' })} label="Agregar suplemento" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {editData.recommended_supplements.map((supp, i) => (
                <div key={i} className="p-2 rounded bg-purple-500/10 border border-purple-500/20">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-white font-semibold text-xs flex items-center gap-1.5">
                        {supp.name}
                        {supp.requires_validation && (
                          <span className="text-amber-400 text-xs font-normal">(requiere validacion)</span>
                        )}
                      </p>
                      <p className="text-purple-300/70 text-xs mt-0.5">{supp.clinical_note || supp.reason}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-purple-300 text-xs font-medium">{supp.dose}</span>
                      {supp.frequency && <p className="text-purple-400/60 text-xs">{supp.frequency}</p>}
                      {supp.timing && <p className="text-purple-400/50 text-xs">{supp.timing}</p>}
                    </div>
                  </div>
                </div>
              ))}
              {editData.recommended_supplements.length === 0 && <p className="text-purple-300/50 text-xs">Sin suplementacion recomendada especifica</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 8: SUPLEMENTOS A EVITAR / USAR CON PRECAUCION ===== */}
        <EditableSection
          title="SUPLEMENTOS A EVITAR / USAR CON PRECAUCION"
          icon={FaExclamationTriangle}
          iconColor="#ef4444"
          bgColor="rgba(239, 68, 68, 0.08)"
          borderColor="rgba(239, 68, 68, 0.25)"
          count={editData.avoid_supplements?.length}
          testId="clinical-edit-avoid-supplements"
        >
          {editMode ? (
            <div className="space-y-2">
              {editData.avoid_supplements.map((supp, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Input
                    value={supp.name}
                    onChange={(e) => {
                      const updated = [...editData.avoid_supplements];
                      updated[i] = { ...updated[i], name: e.target.value };
                      updateField('avoid_supplements', updated);
                    }}
                    className="bg-gray-900/50 border-red-500/30 text-red-300 text-xs font-semibold w-1/3"
                    placeholder="Suplemento..."
                  />
                  <Input
                    value={supp.reason}
                    onChange={(e) => {
                      const updated = [...editData.avoid_supplements];
                      updated[i] = { ...updated[i], reason: e.target.value };
                      updateField('avoid_supplements', updated);
                    }}
                    className="bg-gray-900/50 border-red-500/20 text-red-200/60 text-xs flex-1"
                    placeholder="Motivo de precaucion..."
                  />
                  <button onClick={() => removeListItem('avoid_supplements', i)} className="text-red-400 hover:text-red-300 p-1 mt-1">
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
              <AddItemButton onClick={() => addListItem('avoid_supplements', { name: '', reason: '', requires_validation: false, clinical_note: '' })} label="Agregar suplemento a evitar" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {editData.avoid_supplements.map((supp, i) => (
                <div key={i} className="p-2 rounded bg-red-500/10 border border-red-500/20">
                  <div className="flex items-start gap-2">
                    <FaExclamationTriangle className="text-red-400 text-xs mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-red-300 font-semibold text-xs flex items-center gap-1.5">
                        {supp.name}
                        {supp.requires_validation && (
                          <span className="text-amber-400 text-xs font-normal">(requiere validacion)</span>
                        )}
                      </p>
                      <p className="text-red-200/60 text-xs">{supp.reason}</p>
                      {supp.clinical_note && supp.clinical_note !== supp.reason && (
                        <p className="text-red-200/40 text-xs mt-0.5">{supp.clinical_note}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {editData.avoid_supplements.length === 0 && <p className="text-red-300/50 text-xs">Sin suplementos a evitar especificos</p>}
            </div>
          )}
        </EditableSection>

        {/* ===== BLOCK 9: NOTAS ADICIONALES MANUALES ===== */}
        <div className="rounded-lg p-2.5" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <p className="text-purple-400 font-bold text-xs mb-2 flex items-center gap-2">
            <FaStickyNote /> NOTAS ADICIONALES (manual)
          </p>
          <Textarea
            value={editData.manual_notes || clinicalNotes}
            onChange={(e) => {
              const val = e.target.value;
              updateField('manual_notes', val);
              setClinicalNotes(val);
            }}
            placeholder="Escribe notas clinicas adicionales para este paciente..."
            className="bg-gray-900/50 border-purple-500/30 text-white text-xs min-h-[60px] placeholder:text-purple-300/40"
            data-testid="clinical-manual-notes"
          />
        </div>

        {/* ===== BLOCK 9.5: ALIMENTOS PROHIBIDOS MANUALMENTE =====
            Esta caja ahora vive como bloque independiente fuera del panel clínico
            (siempre visible para cualquier cliente, tenga o no patología).
            Se deja deshabilitada aquí para evitar duplicado en pantalla. */}

        {/* ===== INDIVIDUALIZATION LAYER ===== */}
        <IndividualizationPanel editingPlanId={editingPlanId} clinicalResult={clinicalResult} onFoodReauthorized={onFoodReauthorized} onFoodReblocked={onFoodReblocked} />

      </CardContent>
    </Card>
  );
};

export default ClinicalEditPanel;
